import type { MutableRefObject } from 'react';
import { useConnectCredentialsStore } from '../../stores/session/connectCredentialsStore';
import { useSessionStore } from '../../stores/session/sessionStore';
import { useTenantStore } from '../../stores/tenantStore';
import { oidcUserManager } from '../../services/oidcService';
import { refreshAcceoConnectToken } from '../../services/acceoConnectService';
import { getDesktopAssertion } from '../../services/desktopAssertion';
import { assertionMatchesPinnedCompany } from '../../services/bridgeLaunchContext';
import { isDesktopSessionKind } from '../../hooks/useIsAcomba';

const REFRESH_BEFORE_EXPIRY_MS = 5 * 60 * 1000; // 5 minutes

let acceoConnectRefreshInFlight = false;
let oidcRefreshInFlight = false;

/**
 * signinRedirect() that carries the desktop launch assertion in OIDC state. A desktop session without a
 * usable assertion stays on its app token: a stateless login would land it on the AcombaGO tenant picker.
 */
async function signinRedirectPreservingDesktopContext(): Promise<void> {
    if (!isDesktopSessionKind(useSessionStore.getState().sessionKind)) {
        await oidcUserManager.signinRedirect();
        return;
    }
    const assertion = getDesktopAssertion();
    if (assertion === null || !assertionMatchesPinnedCompany(assertion)) {
        console.warn('OIDC session lost with no desktop launch assertion to carry; keeping the desktop app session.');
        return;
    }
    await oidcUserManager.signinRedirect({ state: { src: assertion } });
}

// ─── AcceoConnect token auto-refresh ─────────────────────────────────────────
// Fires 5 min before the AcceoConnect token expires. Uses the portal IS JWT for
// portal sessions, or the current OIDC access token for standard sessions.

async function doAcceoConnectRefresh(): Promise<void> {
    if (acceoConnectRefreshInFlight) return;
    acceoConnectRefreshInFlight = true;
    try {
        const { portalIsToken: pt, setAcceoConnectToken: st, setSaasSessionToken: sst } = useConnectCredentialsStore.getState();
        const { tenantCode: tc } = useTenantStore.getState();
        if (!tc) return;
        if (pt) {
            try {
                const result = await refreshAcceoConnectToken(pt, tc);
                if (result) { st(result.access_token, result.expires_in); sst(result.saasSessionToken ?? null); }
                else { console.error('Proactive AcceoConnect refresh returned null (portal).'); }
            } catch (e) { console.error('Proactive AcceoConnect refresh failed (portal):', e); }
            return;
        }
        const oidcUser = await oidcUserManager.getUser();
        if (!oidcUser || oidcUser.expired || !oidcUser.access_token) {
            try {
                await signinRedirectPreservingDesktopContext();
            } catch (e) {
                console.error('signinRedirect failed:', e);
            }
            return;
        }
        try {
            const result = await refreshAcceoConnectToken(oidcUser.access_token, tc);
            if (result) { st(result.access_token, result.expires_in); sst(result.saasSessionToken ?? null); }
        } catch (e) { console.error('Proactive AcceoConnect refresh failed (OIDC):', e); }
    } finally {
        acceoConnectRefreshInFlight = false;
    }
}

export function scheduleAcceoConnectRefresh(timerRef: MutableRefObject<ReturnType<typeof setTimeout> | null>): void {
    if (timerRef.current) clearTimeout(timerRef.current);
    const { expiresAt } = useConnectCredentialsStore.getState();
    if (expiresAt == null) return;
    const msUntilRefresh = expiresAt - Date.now() - REFRESH_BEFORE_EXPIRY_MS;

    if (msUntilRefresh <= 0) {
        void doAcceoConnectRefresh(); // already within the window — refresh immediately
        return;
    }

    timerRef.current = setTimeout(() => void doAcceoConnectRefresh(), msUntilRefresh);
}

// ─── OIDC session auto-refresh ────────────────────────────────────────────────
// Fires 5 min before the OIDC token expires, independently of the AcceoConnect
// refresh above. Keeps the OIDC session alive so AcceoConnect refresh always
// finds a valid OIDC user and never needs to fall back to signinRedirect.
// Skipped for portal and Acomba desktop sessions, which don't use the OIDC token after login.

async function doOidcRefresh(
    timerRef: MutableRefObject<ReturnType<typeof setTimeout> | null>,
    mountedRef: MutableRefObject<boolean>,
): Promise<void> {
    if (oidcRefreshInFlight) return;
    oidcRefreshInFlight = true;
    try {
        const silentUser = await oidcUserManager.signinSilent();
        if (!mountedRef.current) return;
        await scheduleOidcRefresh(timerRef, mountedRef); // re-schedule for the newly issued token
        // Re-exchange for a fresh AcceoConnect token using the access token returned by
        // signinSilent() directly — avoids a redundant getUser() storage read.
        // The old AcceoConnect token is tied to the previous OIDC session and the server
        // invalidates it on renewal.
        if (silentUser?.access_token) {
            const { tenantCode: tc } = useTenantStore.getState();
            const { setAcceoConnectToken: st, setSaasSessionToken: sst } = useConnectCredentialsStore.getState();
            if (tc) {
                try {
                    const result = await refreshAcceoConnectToken(silentUser.access_token, tc);
                    if (result) { st(result.access_token, result.expires_in); sst(result.saasSessionToken ?? null); }
                    else { console.error('AcceoConnect refresh after OIDC renewal returned null.'); }
                } catch (e) { console.error('AcceoConnect refresh after OIDC renewal failed:', e); }
            }
        }
    } catch (e) {
        console.error('OIDC silent refresh failed:', e);
        try {
            await signinRedirectPreservingDesktopContext();
        } catch (redirectError) {
            console.error('signinRedirect failed after OIDC silent refresh error:', redirectError);
        }
    } finally {
        oidcRefreshInFlight = false;
    }
}

export async function scheduleOidcRefresh(
    timerRef: MutableRefObject<ReturnType<typeof setTimeout> | null>,
    mountedRef: MutableRefObject<boolean>,
): Promise<void> {
    if (!mountedRef.current) return;
    if (timerRef.current) clearTimeout(timerRef.current);
    // Only the browser flow needs a live OIDC token after login (AcceoConnect refresh, X-Sso-Bearer).
    // Portal rides the host IS JWT; Acomba desktop runs on its app token alone.
    const { sessionKind } = useSessionStore.getState();
    if (sessionKind === 'portal' || isDesktopSessionKind(sessionKind)) return;

    const oidcUser = await oidcUserManager.getUser();
    // expires_at == null means no expiry info — skip. expires_at === 0 is a valid (past) timestamp.
    if (oidcUser?.expires_at == null) return;

    const msUntilRefresh = oidcUser.expires_at * 1000 - Date.now() - REFRESH_BEFORE_EXPIRY_MS;

    if (msUntilRefresh <= 0) {
        void doOidcRefresh(timerRef, mountedRef); // already within the window — refresh immediately
        return;
    }

    timerRef.current = setTimeout(() => void doOidcRefresh(timerRef, mountedRef), msUntilRefresh);
}
